import { styled } from 'styled-components';

import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { H1 } from '#/components/Text/H1';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useSearchHero } from '#/modules/search/hooks/useSearchHero';
import type { SearchType } from '#/modules/search/types/SearchType';
import { cssBreakpoints, fontSizesByType } from '#/theme/theme';

import {
  SearchHero_Box_1,
  SearchHero_Box_2,
  SearchHero_Box_3,
  SearchHeroBackgroundHolder,
  SearchHeroBackgroundImageHolder,
} from './SearchHero.styled';
import { SearchHeroInputs } from './SearchHeroInputs';

const Title = styled.div`
  line-height: 1.3;
  font-size: ${fontSizesByType.h3[0]}px;

  @media all and (min-width: ${cssBreakpoints.smUp}) {
    font-size: ${fontSizesByType.h3[2]}px;
  }
`;

type Props = {
  title?: string;
  image: ApiFilestackImageData | undefined;
  imageMobile: ApiFilestackImageData | undefined;
  defaultSearchType: SearchType;
};

export function SearchHero({
  title,
  image,
  imageMobile,
  defaultSearchType,
}: Props) {
  const { i18n } = useI18n();

  const {
    initialQuery,
    search,
    searchLocation,
    changeSearchLocation,
    redirectParam,
    showLogin,
    clickLogin,
    clickSignup,
  } = useSearchHero({ searchFrom: 'Search Hero' });

  return (
    <SearchHero_Box_3 data-qa-id="search-hero">
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {(image ?? imageMobile) && (
          <SearchHeroBackgroundHolder>
            <SearchHeroBackgroundImageHolder>
              <FilestackImage
                qaId="hero-image-image"
                width={1920}
                height={1024}
                expandSize
                objectFit="cover"
                alt={i18n('Hero image')}
                data={image ?? imageMobile ?? null}
                mobileData={imageMobile ?? undefined}
                mobileAspectRatio={{ width: 414, height: 470 }}
                eagerLoad
                style={{ height: '100%' }}
              />
            </SearchHeroBackgroundImageHolder>
          </SearchHeroBackgroundHolder>
        )}
      </div>

      <div
        style={{
          position: 'relative',
          transform: 'rotateZ(0)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <SearchHero_Box_2 data-qa-id="search-hero-menu">
          <SearchHero_Box_1>
            <div style={{ marginBottom: 20 }}>
              <H1>
                <Title
                  dangerouslySetInnerHTML={{
                    __html: title?.replace(/<(\/?)(p|h1)>/g, '<$1span>') ?? '',
                  }}
                />
              </H1>
            </div>

            <SearchHeroInputs
              initialQuery={initialQuery}
              defaultSearchType={defaultSearchType}
              onSearch={search}
              searchLocation={searchLocation}
              onChangeSearchLocation={changeSearchLocation}
              redirectParam={redirectParam}
              clickLogin={clickLogin}
              showLogin={showLogin}
              clickSignup={clickSignup}
            />
          </SearchHero_Box_1>
        </SearchHero_Box_2>
      </div>
    </SearchHero_Box_3>
  );
}
